import React, { useEffect, useState } from 'react';
import EnhancedTable from 'src/components/enhanced-table/EnhancedTable';
import {
  EnhanceTableHeaderTypes,
  EnhancedTableColumnType,
  ITableMenuAction
} from 'src/components/enhanced-table';
import { Helmet } from 'react-helmet-async';
import MainPageTitle from 'src/components/layout-components/main-layout/MainPageTitle';
import Loader from 'src/components/Loader';
import { DataService } from 'src/services/data-service';
import { IConfirmation } from 'src/redux/confirmation/types';
import { OpenConfirmation } from 'src/redux/confirmation/confirmationReducer';
import { useAppDispatch } from 'src/redux/hooks';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import CancelIcon from '@mui/icons-material/Cancel';
import ImageIcon from '@mui/icons-material/Image';
import { Dialog, DialogContent, DialogTitle, IconButton } from '@mui/material';
import ChangeCircleIcon from '@mui/icons-material/ChangeCircle';
import EditIcon from '@mui/icons-material/Edit';

interface INewsListing {
  id: string;
  content: string;
  title: string;
  imageUrl: string;
  status: string;
  createdOn: Date;
}

interface INewsListingResponse {
  id: string;
  content: string;
  title: string;
  imageUrl: string;
  active: string;
  createdOn: Date;
}

const NewsListing: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [loading, setLoading] = useState<boolean>(true);
  const [data, setData] = useState<Record<string, INewsListing>>();
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const headers: EnhanceTableHeaderTypes[] = [
    { id: 'title', label: 'Title',type: EnhancedTableColumnType.Clickable,
      onClick: (_: string, row: Record<string, any>) => {
        handleEdit(row.id);
        console.log('Navigating to edit:', row.id);
      } },
    // {
    //   id: 'content',
    //   label: 'content',
    //   type: EnhancedTableColumnType.TEXT
    // },
    {
      id: 'createdOn',
      label: 'Created On',
      type: EnhancedTableColumnType.DATE
    },
    {
      id: 'status',
      label: 'Status',
      type: EnhancedTableColumnType.COLORED_CHIP,
      chipColors: {
        Active: { backgroundColor: 'green', color: 'white' },
        Inactive: { backgroundColor: 'red', color: 'white' }
      }
    },
    {
      id: 'actions',
      label: 'Actions',
      type: EnhancedTableColumnType.Action,
      actions: [
        {
          onClick: (id) => PreviewImage(id),
          icon: <ImageIcon fontSize="small" />,
          label: 'View Image',
          hidden: (r) => false
        },
        {
          onClick: (id: string) => handleEdit(id),
          icon: <EditIcon fontSize="small" />,
          label: 'Edit',
          hidden: () => false
        },
        {
          onClick: (id: string) => changeStatus(id),
          icon: <ChangeCircleIcon fontSize="small" />,
          label: 'Toggle Status',
          hidden: () => false
        }
      ]
    }
  ];

  const PreviewImage = (id: string) => {
    const image = data ? data[id] : null;
    if (image) {
      console.log(image);
      setPreviewImage(image.imageUrl);
    }
  };

   const changeStatus = async (id: string) => {
      const article = data[id];
      if (!article) return;
      if (article.status === 'Active') {
        const response = await DataService.patch(
          `api/article/deactivate?id=${id}`,
          {}
        );
        if (response.ok) {
          toast.success('News deactivated successfully');
          initialize();
        } else {
          toast.error('Failed to deactivate News');
        }
      } else {
        // If inactive, call the activate endpoint
        const response = await DataService.patch(`api/article/activate?id=${id}`, {});
        if (response.ok) {
          toast.success('News activated successfully');
          initialize();
        } else {
          toast.error('Failed to activate new');
        }
      }
    };

  const initialize = async () => {
    setLoading(true);
    const response = await DataService.get('api/article/all');
    if (response.ok) {
      const list: INewsListingResponse[] = await response.json();
      const result: Record<string, INewsListing> = {};
      list.forEach((c) => {
        result[c.id] = {
          id: c.id,
          content: c.content,
          title: c.title,
          imageUrl: c.imageUrl,
          createdOn: c.createdOn,
          status: c.active ? 'Active' : 'Inactive'
        };
      });
      console.log(list);
      setData(result);
    } else {
      toast.error('Failed to fetch articles.');
    }
    setLoading(false);
  };
  const activate = (id: string) => {
    const confimation: IConfirmation = {
      open: true,
      title: 'Confirmation',
      message: 'Do you want to activate this News?',
      onSubmit: async () => {
        const result = await DataService.patch(
          'api/article/activate?id=' + id,
          {
            id: id
          }
        );
        if (result.ok) {
          initialize();
          toast.success('Success!');
        } else {
          toast.error('An error occured');
        }
      }
    };

    dispatch(OpenConfirmation(confimation));
  };

  const deactivate = (id: string) => {
    const confimation: IConfirmation = {
      open: true,
      title: 'Confirmation',
      message: 'Do you want to deactivate this user?',
      onSubmit: async () => {
        const result = await DataService.patch(
          'api/article/deactivate?id=' + id,
          {
            id: id
          }
        );
        if (result.ok) {
          initialize();
          toast.success('Success!');
        } else {
          toast.error('An error occured');
        }
      }
    };

    dispatch(OpenConfirmation(confimation));
  };
  useEffect(() => {
    initialize();
  }, []);

  if (loading) {
    return <Loader size={75} />;
  }

  // Navigate to Edit articles
  const handleEdit = (id: string) => {
    navigate(`/web-builder/news/edit/${id}`);
  };

  // Navigate to Add articles
  const handleAdd = () => {
    navigate('/web-builder/news/add');
  };

  const actions: ITableMenuAction[] = [
    {
      key: 'addArticles',
      title: 'Add News',
      disabled: (selected: string[]) => selected.length > 0,
      onClick: handleAdd
    },
    {
      key: 'editArticle',
      title: 'Edit News',
      disabled: (selected: string[]) => selected.length === 0,
      onClick: (selected) => {
        if (selected.length > 0) {
          handleEdit(selected[0]);
        }
      }
    }
  ];

  return (
    <div>
      <Helmet>
        <title>Articles Listing</title>
      </Helmet>
      <MainPageTitle
        title="News List"
        action={{
          title: 'Add News',
          onClick: handleAdd
        }}
      />
      <Dialog open={!!previewImage} onClose={() => setPreviewImage(null)}>
        <DialogTitle>Image Preview</DialogTitle>
        <DialogContent>
          {previewImage && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}
            >
              <img
                src={previewImage}
                alt="Preview"
                style={{
                  width: '100%',
                  maxHeight: '500px',
                  marginBottom: '10px'
                }}
              />
              <button
                onClick={() => {
                  navigator.clipboard.writeText(previewImage);
                  toast.success('Image link copied to clipboard!');
                }}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Copy Link
              </button>
            </div>
          )}
        </DialogContent>
        <IconButton
          onClick={() => setPreviewImage(null)}
          style={{ position: 'absolute', top: 10, right: 10 }}
        >
          ✖
        </IconButton>
      </Dialog>
      <EnhancedTable
        title="News"
        header={headers}
        data={data}
        actions={actions}
      />
    </div>
  );
};

export default NewsListing;
